import React, { useState } from "react";
import kamlaLogo from '../assests/kamlaLogo.png';
import '../styles/main.css';
import { API_BASE } from "../config";

// --- Utility Functions ---

// 1. Number to Words (Indian System)
function numberToWords(num) {
  if (num == null) return "-";
  num = Number(num);
  if (isNaN(num)) return "-";

  const ones = [
    '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
    'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen',
    'Seventeen', 'Eighteen', 'Nineteen'
  ];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty',
    'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  const scales = ['', 'Thousand', 'Lakh', 'Crore'];

  function chunkToWords(n) {
    let word = "";
    if (n > 99) {
      word += ones[Math.floor(n / 100)] + " Hundred";
      n = n % 100;
      if (n) word += " and ";
    }
    if (n < 20) {
      word += ones[n];
    } else {
      word += tens[Math.floor(n / 10)];
      if (n % 10) word += " " + ones[n % 10];
    }
    return word;
  }

  function splitIndianNumber(n) {
    const str = n.toString();
    let chunks = [];
    let end = str.length;
    chunks.push(parseInt(str.substring(Math.max(0, end - 3), end), 10));
    end -= 3;
    while (end > 0) {
      let start = Math.max(0, end - 2);
      chunks.push(parseInt(str.substring(start, end), 10));
      end -= 2;
    }
    return chunks;
  }

  if (num === 0) return "Zero";
  const numberChunks = splitIndianNumber(parseInt(num));

  let wordArray = [];
  for (let i = 0; i < numberChunks.length; i++) {
    if (numberChunks[i] > 0) {
      wordArray.push(chunkToWords(numberChunks[i]) + (scales[i] ? " " + scales[i] : ""));
    }
  }
  return wordArray.reverse().join(" ").trim();
}

// 2. Date Formatter (DD-MM-YYYY)
function formatDateToDDMMYYYY(dateString) {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (isNaN(date)) return "";
  const day = ("0" + date.getDate()).slice(-2);
  const month = ("0" + (date.getMonth() + 1)).slice(-2);
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
}

// 3. Time Formatter
const formatIndianTime = (dateString) => {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      return date.toLocaleTimeString("en-IN", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true
      });
    }
  } catch (e) {
    return "";
  }
  return "";
};

const ReceiptModal = ({
  show,
  data,
  companyDetails,
  onClose,
  onPrint,
}) => {
  const [showHeader, setShowHeader] = useState(true);

  if (!show || !data || !companyDetails) return null;

  const handlePrint = () => {
    if (onPrint) onPrint();
    else window.print();
  };

  const handleToggleHeader = () => {
    setShowHeader(!showHeader);
  };

  // --- Data Mapping ---
  const patientName = data.patientName || data.name || data.PatientName || "";
  const mobileNo = data.mobileNo || data.MobileNo || "";
  const address = data.address || "";
  const ageGender = `${data.age || "-"}/${data.gender || "-"}`;
  
  // Date and Time
  const regDate = data.regDate || data.ipdDate || data.opdDate || "";
  const receiptDateStr = formatDateToDDMMYYYY(regDate);
  const receiptTimeStr = formatIndianTime(data.created_On   || data.createdOn || data.created_By);

  // ✅ FINANCIALS FIX (Discount in % handled correctly)
  const totalAmount = Number(data.consultFee) || 0;
  
  // डेटाबेस में डिस्काउंट % में सेव है (जैसे 10), तो हम उसे रुपये में बदल रहे हैं
  const discountPercent = Number(data.discount) || 0;
  const discountValRupees = (totalAmount * discountPercent) / 100; 

  // डेटाबेस से सीधे Payable और Due लें ताकि फॉर्म से एकदम मैच करे
  const netPayable = data.payable !== undefined && data.payable !== null ? Number(data.payable) : (totalAmount - discountValRupees); 
  const receivedAmount = Number(data.received) || 0;
  const dueAmount = data.dues !== undefined && data.dues !== null ? Number(data.dues) : Math.max(0, netPayable - receivedAmount);

  let receiptLabel = "Receipt No";
  let receiptNo = "-";

  if (data.opD_SRNO || data.OPD_SRNO) {
      receiptLabel = "OPD Id";
      receiptNo = data.opD_SRNO || data.OPD_SRNO;
  } else if (data.ipD_SRNO || data.IPD_SRNO) {
      receiptLabel = "IPD Id";
      receiptNo = data.ipD_SRNO || data.IPD_SRNO;
  } else {
      receiptNo = data.id || "-";
  }

  // Single row for the table
  const serviceItem = {
    name: "Consultation / Hospital Services", 
    qty: 1,
    rate: totalAmount,
    amount: totalAmount
  };

  // 🌐 Base URL for dynamic images (Bulletproof method)
  const getImageUrl = (path) => {
    if (!path) return "";
    const base = API_BASE.replace(/\/api\/?$/, ''); 
    const cleanPath = path.replace(/^\//, ''); 
    return `${base}/${cleanPath}`;
  };

  // ✅ SAFELY HANDLE 'logo' OR 'Logo' (Case Sensitivity Fix)
  const mainLogoPath = companyDetails?.logo || companyDetails?.Logo;

  return (
    <>
      {/* =========================================
         INLINE CSS: PRINT & MARG BILLING STYLES
      ========================================= */}
      <style>{`
        :root {
          --marg-border: 1px solid #000;
          --marg-border-thick: 2px solid #000;
          --marg-font: Arial, Helvetica, sans-serif;
        }

        .marg-receipt-wrapper {
          font-family: var(--marg-font);
          color: #000;
          background: #fff;
          font-size: 11px; 
        }

        /* ✅ FIX: Box-sizing for perfect percentage alignment */
        .marg-receipt-wrapper * {
          box-sizing: border-box;
        }

        .marg-outer-box {
          border: var(--marg-border-thick);
          margin-bottom: 20px;
          height: 110mm; /* रसीद की कुल ऊंचाई (Half Page/A5) */
          width: 100%;
          box-sizing: border-box;
          position: relative;
          background: #fff;
          display: flex;
          flex-direction: column;
          page-break-inside: avoid;
        }

        .marg-header {
          border-bottom: var(--marg-border-thick);
          padding: 4px 10px;
          text-align: center;
          position: relative;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        
        .marg-title-strip {
          background-color: #e0e0e0 !important; 
          border-bottom: var(--marg-border-thick);
          text-align: center;
          padding: 2px;
          font-weight: 900;
          font-size: 13px;
          letter-spacing: 1px;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }

        .marg-patient-box {
          display: flex;
          border-bottom: var(--marg-border-thick);
        }

        .marg-patient-col { padding: 2px 8px; }
        
        /* ✅ ALIGNMENT FIX: 60% Left Side */
        .marg-patient-left { width: 60%; border-right: var(--marg-border-thick); }
        
        /* ✅ ALIGNMENT FIX: 40% Right Side */
        .marg-patient-right { width: 40%; }

        .marg-patient-col table {
          width: 100%;
          font-size: 11px;
          line-height: 1.3;
        }
        .marg-patient-col table td { vertical-align: top; padding: 1px 0;}

        /* Items Table */
        .marg-table-container {
          flex-grow: 1; /* ✅ Fills remaining space */
          display: flex;
          flex-direction: column;
          width: 100%;
        }

        .marg-table-header {
          display: flex;
          width: 100%;
          border-bottom: var(--marg-border);
          font-weight: bold;
          text-transform: uppercase;
          font-size: 10px;
          background-color: #e0e0e0 !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }

        .marg-table-row {
          display: flex;
          width: 100%;
          font-size: 11px;
        }

        .marg-table-filler {
          flex-grow: 1;
          display: flex;
          width: 100%;
          align-items: stretch; /* ✅ Forces vertical lines down */
        }

        /* ✅ ALIGNMENT FIX: 6% + 54% = 60% */
        .marg-col-sr   { width: 6%;  text-align: center; padding: 2px 5px; border-right: var(--marg-border); }
        .marg-col-desc { width: 54%; text-align: left;   padding: 2px 5px; border-right: var(--marg-border); }
        
        /* ✅ ALIGNMENT FIX: 10% + 12% + 18% = 40% */
        .marg-col-qty  { width: 10%; text-align: center; padding: 2px 5px; border-right: var(--marg-border); }
        .marg-col-rate { width: 12%; text-align: right;  padding: 2px 5px; border-right: var(--marg-border); }
        .marg-col-amt  { width: 18%; text-align: right;  padding: 2px 5px; }

        /* Footer Sections */
        .marg-footer-grid {
          display: flex;
          border-top: var(--marg-border-thick);
          margin-top: auto;
        }

        .marg-words-box {
          width: 60%; /* ✅ Matches left side */
          border-right: var(--marg-border-thick);
          padding: 6px 8px;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
        }

        .marg-calc-box { width: 40%; } /* ✅ Matches right side */
        .marg-calc-table { width: 100%; border-collapse: collapse; }
        .marg-calc-table td { padding: 2px 8px; border-bottom: var(--marg-border); font-size: 11px; }
        .marg-calc-table tr:last-child td { border-bottom: none; }
        
        .marg-highlight-row td {
          background-color: #e0e0e0 !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }

        /* Signatures */
        .marg-signature-box {
          display: flex;
          justify-content: space-between;
          padding: 15px 15px 5px 15px;
          border-top: var(--marg-border-thick);
          background: #fff;
        }

        .marg-sig-line {
          border-top: 1px solid #000;
          padding-top: 3px;
          text-align: center;
          font-weight: bold;
          font-size: 10px;
          width: 100%;
        }

        /* =========================================
           PRINT MEDIA QUERIES
        ========================================= */
        @media print {
          @page { 
            size: A4 portrait; 
            margin: 2mm 5mm 5mm 5mm; 
          }
          html, body {
            height: 100% !important;
            overflow: hidden !important; 
            background: white !important;
            margin: 0 !important;
            padding: 0 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          body * { visibility: hidden; }
          #marg-print-section, #marg-print-section * { visibility: visible; }
          
          #marg-print-section {
            position: absolute !important;
            left: 0 !important; 
            top: 0 !important; 
            width: 100% !important; 
            margin: 0 !important; 
            padding: 0 !important;
          }

          .modal, .modal-dialog, .modal-content, .modal-body {
            border: none !important; 
            box-shadow: none !important;
            background: transparent !important; 
            padding: 0 !important;
            margin: 0 !important;
            position: static !important;
            transform: none !important;
          }
          
          .marg-outer-box {
            margin-top: 0 !important; 
            margin-bottom: 0 !important;
          }

          .no-print { display: none !important; }
        }
      `}</style>

      <div className="modal fade show" style={{ display: "block", backgroundColor: "rgba(0,0,0,0.6)" }}>
        <div className="modal-dialog" style={{ maxWidth: '750px', margin: '20px auto' }}>
          <div className="modal-content border-0">
            
            <div id="marg-print-section" className="marg-receipt-wrapper p-2 bg-white">
              
              <div className="marg-outer-box">
                
                {/* --- Header --- */}
                {showHeader ? (
                  <div className="marg-header">
                    <div  style={{ width: '70px', height: '70px' }}>
                      {/* {kamlaLogo && (
                        <img 
                          src={kamlaLogo} 
                          alt="Logo" 
                          style={{ width: '100%', height: '100%', objectFit: 'contain' }} 
                        />
                      )} */}

       {mainLogoPath && (
    <img 
      src={getImageUrl(mainLogoPath)} 
      alt="Hospital Logo" 
      style={{ width: '100%', height: '100%', objectFit: 'contain' }} 
    />
)}
                    </div>
                    <div style={{ flexGrow: 1 }}>
                      <h2 style={{ margin: '0 0 3px 0', fontWeight: '900', fontSize: '18px', textTransform: 'uppercase' ,color: '#ff0000'}}>
                        {companyDetails.company_Name}
                      </h2>
                      <p style={{ margin: '0', fontSize: '11px' }}>{companyDetails.address}</p>
                      <p style={{ margin: '2px 0 0 0', fontSize: '11px' }}>
                        <strong>Ph:</strong> {companyDetails.contact1} &nbsp;|&nbsp; <strong>Email:</strong> {companyDetails.email_ID}
                      </p>
                    </div>
                    <div style={{ width: '80px', textAlign: 'right', fontSize: '10px' }}>
                      <strong>Reg No:</strong><br/>{companyDetails.reg_No || 'N/A'}
                    </div>
                  </div>
                ) : (
                  <div style={{ height: "80px" }}></div>
                )}

                <div className="marg-title-strip">
                  PAYMENT RECEIPT
                </div>

                {/* --- Patient Info Box --- */}
                <div className="marg-patient-box">
                  <div className="marg-patient-col marg-patient-left">
                    <table>
                      <tbody>
                        <tr>
                          <td width="25%"><strong>Name</strong></td>
                          <td width="5%">:</td>
                          <td><strong style={{ fontSize: '12px', textTransform: 'uppercase' }}>{patientName}</strong></td>
                        </tr>
                        <tr>
                          <td><strong>UHID No.</strong></td>
                          <td>:</td>
                          <td>{data.uhidNo || data.UHID_No || data.uhiD_No || "-"}</td>
                        </tr>
                        <tr>
                          <td><strong>Mobile No.</strong></td>
                          <td>:</td>
                          <td>{mobileNo || "-"}</td>
                        </tr>
                        <tr>
                          <td><strong>Address</strong></td>
                          <td>:</td>
                          <td>{address || "-"}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Right Aligned Data */}
                  <div className="marg-patient-col marg-patient-right">
                    <table style={{ width: '100%' }}>
                      <tbody>
                        <tr>
                          <td width="55%" style={{ textAlign: 'right' }}><strong>{receiptLabel}</strong></td>
                          <td width="5%" style={{ textAlign: 'center' }}>:</td>
                          <td style={{ textAlign: 'left' }}><strong>{receiptNo}</strong></td>
                        </tr>
                        <tr>
                          <td style={{ textAlign: 'right' }}><strong>Date</strong></td>
                          <td style={{ textAlign: 'center' }}>:</td>
                          <td style={{ textAlign: 'left' }}>{receiptDateStr}</td>
                        </tr>
                        <tr>
                          <td style={{ textAlign: 'right' }}><strong>Time</strong></td>
                          <td style={{ textAlign: 'center' }}>:</td>
                          <td style={{ textAlign: 'left' }}>{receiptTimeStr}</td>
                        </tr>
                        <tr>
                          <td style={{ textAlign: 'right' }}><strong>Age/Sex</strong></td>
                          <td style={{ textAlign: 'center' }}>:</td>
                          <td style={{ textAlign: 'left' }}>{ageGender}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* --- Bill Items Table --- */}
                <div className="marg-table-container">
                  
                  <div className="marg-table-header">
                    <div className="marg-col-sr">S.No.</div>
                    <div className="marg-col-desc">Particulars / Services</div>
                    <div className="marg-col-qty">Qty</div>
                    <div className="marg-col-rate">Rate (Rs.)</div>
                    <div className="marg-col-amt">Amount (Rs.)</div>
                  </div>

                  <div className="marg-table-body">
                    {/* 1. Actual Service Row */}
                    <div className="marg-table-row">
                      <div className="marg-col-sr">1</div>
                      <div className="marg-col-desc"><strong>{serviceItem.name}</strong></div>
                      <div className="marg-col-qty">{serviceItem.qty}</div>
                      <div className="marg-col-rate">{serviceItem.rate.toFixed(2)}</div>
                      <div className="marg-col-amt"><strong>{serviceItem.amount.toFixed(2)}</strong></div>
                    </div>
                  </div>

                  {/* ✅ Empty filler to stretch vertical lines safely */}
                  <div className="marg-table-filler">
                    <div className="marg-col-sr"></div>
                    <div className="marg-col-desc"></div>
                    <div className="marg-col-qty"></div>
                    <div className="marg-col-rate"></div>
                    <div className="marg-col-amt"></div>
                  </div>

                </div>

                {/* --- Summary & Footer --- */}
                <div className="marg-footer-grid">
                  <div className="marg-words-box">
                    <div>
                      <p style={{ margin: "0 0 2px 0", fontSize: "10px" }}><strong>Total Amount in Words:</strong></p>
                      <p style={{ margin: "0", fontSize: "11px", fontWeight: "bold", textTransform: "capitalize" }}>
                        Rupees {numberToWords(receivedAmount > 0 ? receivedAmount : netPayable)} Only
                      </p>
                    </div>
                    <div style={{ fontSize: "9px", color: "#444" }}>
                      <p style={{ margin: "0" }}><strong>Disclaimer:</strong></p>
                      <ul style={{ paddingLeft: "12px", margin: "2px 0 0 0" }}>
                        <li>Payment once made is non-refundable.</li>
                        <li>Generated By: {data.createdBy || data.created_By || "System"}</li>
                      </ul>
                    </div>
                  </div>
                  
                  <div className="marg-calc-box">
                    <table className="marg-calc-table">
                      <tbody>
                        <tr>
                          <td>Total Amount</td>
                          <td style={{ textAlign: "right", fontWeight: "bold" }}>{totalAmount.toFixed(2)}</td>
                        </tr>
                        
                        {/* ✅ FIXED DISCOUNT ROW */}
                        {discountValRupees > 0 && (
                          <tr>
                            <td>Discount ({discountPercent}%)</td>
                            <td style={{ textAlign: "right" }}>- {discountValRupees.toFixed(2)}</td>
                          </tr>
                        )}
                        
                        <tr className="marg-highlight-row">
                          <td><strong>Net Payable</strong></td>
                          <td style={{ textAlign: "right", fontWeight: "bold", fontSize: "12px" }}>{netPayable.toFixed(2)}</td>
                        </tr>
                        <tr>
                          <td>Paid Amount</td>
                          <td style={{ textAlign: "right" }}>- {receivedAmount.toFixed(2)}</td>
                        </tr>
                        <tr style={{ borderTop: "var(--marg-border-thick)" }}>
                          <td><strong>Balance Due</strong></td>
                          <td style={{ textAlign: "right", fontWeight: "bold", fontSize: "12px" }}>{dueAmount.toFixed(2)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Signatures */}
                <div className="marg-signature-box">
                  <div style={{ textAlign: "center", width: "40%", display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
                    <div className="marg-sig-line">Patient / Attendant</div>
                  </div>
                  <div style={{ textAlign: "center", width: "40%", display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
                    <div className="marg-sig-line">Authorized Signature</div>
                  </div>
                </div>

              </div>

            </div>

            {/* --- Action Buttons (Hidden in Print) --- */}
            <div className="no-print d-flex justify-content-end gap-2 mt-2 pt-2 border-top bg-light p-3 rounded-bottom">
              <button 
                  className={`btn btn-sm ${showHeader ? "btn-outline-secondary" : "btn-warning"} fw-medium px-3`} 
                  onClick={handleToggleHeader}
              >
                <i className={`bi ${showHeader ? "bi-eye-slash" : "bi-eye"} me-2`}></i>
                {showHeader ? "Use Letterhead (Hide Header)" : "Show Header"}
              </button>
              <button className="btn btn-secondary btn-sm px-4" onClick={onClose}>Close</button>
              <button className="btn btn-primary btn-sm px-4 fw-bold" onClick={handlePrint} style={{ background: 'var(--theme-color, #0088cc)', borderColor: 'var(--theme-color, #0088cc)' }}>
                  <i className="bi bi-printer me-2"></i> Print
              </button>
            </div>

          </div>
        </div>
      </div>
    </>
  );
};

export default ReceiptModal;